// Bulk upload on the photos page (2026-10-05): which walk each file goes to, from its taken time.
// Pure functions over /api/walks rows and /api/photos rows, so they can be tried outside React.
import { COMPARISON_SOURCES, ukDateKey } from './format.js'

// A photo up to this long before a walk starts or after it ends still belongs to it (getting
// ready at the start, a last photo after pressing Stop).
export const MATCH_SLACK_MS = 30 * 60 * 1000

// No walk lasts longer. A guard: two MapMyWalk walks (1850, 1897) had end times in 2080 until
// migration 00011, and caught every later photo.
const MAX_WALK_MS = 12 * 3600 * 1000

const startMs = (w) => Date.parse(w.start_time)
const endMs = (w) => Math.min(Date.parse(w.end_time ?? w.start_time), startMs(w) + MAX_WALK_MS)

// How far a moment is outside a walk: 0 inside it, else ms before the start (negative) or after
// the end (positive).
export function offsetFromWalk(t, walk) {
  if (t < startMs(walk)) return t - startMs(walk)
  if (t > endMs(walk)) return t - endMs(walk)
  return 0
}

// Compares two equal-length arrays element by element.
function isLess(a, b) {
  const i = a.findIndex((v, k) => v !== b[k])
  return i >= 0 && a[i] < b[i]
}

// The walk a file taken at takenAt (ISO) belongs to, or null if none is within MATCH_SLACK_MS.
// Of the walks it falls in or near, the closest wins, then a counted one, then one that isn't a
// comparison recording (Strava, OS Maps, AllTrails). Then, if that walk is one recording of a
// walk recorded several ways, the photo goes on the counted recording, the one the walks table
// shows: a photo during the Pixel's recording lands on the MMW walk it was grouped with.
export function matchWalk(takenAt, walks) {
  if (!takenAt) return null
  const t = Date.parse(takenAt)
  let best = null
  let bestRank = null
  for (const w of walks) {
    const gap = Math.abs(offsetFromWalk(t, w))
    if (gap > MATCH_SLACK_MS) continue
    const rank = [gap, w.is_counted ? 0 : 1, COMPARISON_SOURCES[w.source] ? 1 : 0, startMs(w)]
    if (!bestRank || isLess(rank, bestRank)) {
      best = w
      bestRank = rank
    }
  }
  if (best && best.group_id && !best.is_counted) {
    best = walks.find((w) => w.group_id === best.group_id && w.is_counted) ?? best
  }
  return best
}

// Every recording that started on a UK day ("2026-10-03"), earliest first, for the walk picker.
export function walksOnDay(dayKey, walks) {
  return walks.filter((w) => ukDateKey(w.start_time) === dayKey).sort((a, b) => startMs(a) - startMs(b))
}

// The photo already on the site that this file looks like: the same size and either the same
// file name or the same file time (Android's picker gives a file a new name each time).
export function findDuplicate(file, takenAt, photos) {
  return photos.find((p) => p.size_bytes === file.size &&
    (p.original_name === file.name || (takenAt && p.file_taken_at && Date.parse(p.file_taken_at) === Date.parse(takenAt)))) ?? null
}
